import React, { useEffect, useState } from "react";
import axios from "axios";
import "./SurveyQuestionEditor.css";

export default function SurveyQuestionEditor({ surveyId, surveyTypeId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    loadSurvey();
  }, [surveyId]);

  const loadSurvey = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/question/${surveyId}`
      );

      const api = res.data;

      setMeta({
        survey: api.survey_name,
        company: api.company_name
      });

      if (!api.groups || api.groups.length === 0) {
        setDraft(buildEmptyDraft(api));
      } else {
        setDraft(buildDraft(api));
      }

    } catch (err) {
      console.error(err);
      setDraft({
        surveyId,
        surveyName: "Untitled Survey",
        companyName: null,
        groups: [{
          tempId: crypto.randomUUID(),
          serverGroupName: null,
          name: "New Group",
          order: 0,
          questions: []
        }]
      });
    } finally {
      setLoading(false);
    }
  };

  function buildEmptyDraft(api) {
    return {
      surveyId: api.survey_id,
      surveyName: api.survey_name,
      companyName: api.company_name,
      groups: [
        {
          tempId: crypto.randomUUID(),
          serverGroupName: null,
          name: "New Group",
          order: 0,
          questions: []
        }
      ]
    };
  }

  function buildDraft(api) {
    return {
      surveyId: api.survey_id,
      surveyName: api.survey_name,
      companyName: api.company_name,
      groups: api.groups.map((g, gi) => ({
        tempId: crypto.randomUUID(),
        serverGroupName: g.group,
        name: g.group,
        order: gi,
        questions: g.questions.map((q, qi) => ({
          tempId: crypto.randomUUID(),
          serverId: q.id,
          text: q.question,
          type: q.ans_type,
          options: q.mcq_options || [],
          questionNo: q.question_no || "",
          required: q.textbox_required || false,
          order: qi,
          isNew: false,
          isDeleted: false,
          isModified: false
        }))
      }))
    };
  }

  const saveSurvey = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/survey/questions/${surveyId}`,
        {
          ...draft,
          survey_type_fk: surveyTypeId
        }
      );

      alert("Survey saved successfully");
      loadSurvey(); 

    } catch (err) {
      console.error(err);
      alert("Failed to save survey");
    }
  };

  const addGroup = () => {
    setDraft(prev => ({
      ...prev,
      groups: [
        ...prev.groups,
        {
          tempId: crypto.randomUUID(),
          serverGroupName: null,
          name: "New Group",
          order: prev.groups.length,
          questions: []
        }
      ]
    }));
  };

  const addQuestion = (groupId) => {
    setDraft(prev => ({
      ...prev,
      groups: prev.groups.map(g => {
        if (g.tempId !== groupId) return g;
        return {
          ...g,
          questions: [
            ...g.questions,
            {
              tempId: crypto.randomUUID(),
              serverId: null,
              text: "New Question",
              type: "mcq",
              // Default to the 4 standard options
              options: ["Strongly disagree", "Disagree", "Agree", "Strongly agree"],
              questionNo: "",
              required: false,
              order: g.questions.length,
              isNew: true,
              isDeleted: false,
              isModified: true
            }
          ]
        };
      })
    }));
  };

  const deleteQuestion = (groupId, qId) => {
    setDraft(prev => ({
      ...prev,
      groups: prev.groups.map(g => {
        if (g.tempId !== groupId) return g;
        return {
          ...g,
          questions: g.questions.map(q =>
            q.tempId === qId ? { ...q, isDeleted: true } : q
          )
        };
      })
    }));
  };

  const updateQuestionText = (groupId, qId, text) => {
    setDraft(prev => ({
      ...prev,
      groups: prev.groups.map(g => {
        if (g.tempId !== groupId) return g;
        return {
          ...g,
          questions: g.questions.map(q =>
            q.tempId === qId ? { ...q, text, isModified: true } : q
          )
        };
      })
    }));
  };
  const updateQuestionNo = (groupId, qId, questionNo) => {
  setDraft(prev => ({
    ...prev,
    groups: prev.groups.map(g => {
      if (g.tempId !== groupId) return g;
      return {
        ...g,
        questions: g.questions.map(q =>
          q.tempId === qId
            ? { ...q, questionNo, isModified: true }
            : q
        )
      };
    })
  }));
};

  // ✅ NEW: Function to update individual MCQ options
  const updateQuestionOption = (groupId, qId, optionIndex, newText) => {
    setDraft(prev => ({
      ...prev,
      groups: prev.groups.map(g => {
        if (g.tempId !== groupId) return g;

        return {
          ...g,
          questions: g.questions.map(q => {
            if (q.tempId !== qId) return q;

            // Clone the options array and update the specific index
            const updatedOptions = [...q.options];
            updatedOptions[optionIndex] = newText;

            return { ...q, options: updatedOptions, isModified: true };
          })
        };
      })
    }));
  };

  if (loading) return <div className="editor-loading">Loading survey...</div>;
  if (!draft) return null;

  return (
    <div className="question-editor">
      {/* HEADER */}
      <div className="editor-header">
        <button className="back-btn" onClick={onBack}>← Back</button>
        <h2>{meta?.survey}</h2>
        <div className="company">{meta?.company}</div>
      </div>

      {draft.groups.map((grp) => (
        <div key={grp.tempId} className="group-card">
          <div className="group-title">
            <input
              value={grp.name}
              onChange={e => {
                const name = e.target.value;
                setDraft(prev => ({
                  ...prev,
                  groups: prev.groups.map(g =>
                    g.tempId === grp.tempId ? { ...g, name } : g
                  )
                }));
              }}
            />
            <button onClick={() => addQuestion(grp.tempId)}>+ Question</button>
          </div>

          <div className="question-list">
            {grp.questions.filter(q => !q.isDeleted).map((q, i) => (
              <div key={q.tempId} className="question-card">
                {String(surveyTypeId) === "3" && (
  <input
    type="number"
    placeholder="Question No."
    value={q.questionNo}
    onChange={e =>
      updateQuestionNo(grp.tempId, q.tempId, e.target.value)
    }
    style={{ marginBottom: '10px', display: 'block', width: '100px' }}
  />
)}
                <textarea
                  value={q.text}
                  onChange={e =>
                    updateQuestionText(grp.tempId, q.tempId, e.target.value)
                  }
                />

                {/* ✅ NEW: Editable Options UI if surveyTypeId == 3 and type is MCQ */}
                {String(surveyTypeId) === "3" && q.type === "mcq" && (
                  <div className="options-container" style={{ marginTop: '10px' }}>
                    {q.options && q.options.map((opt, optIndex) => (
                      <div key={optIndex} style={{ marginBottom: '5px' }}>
                        <input
                          type="text"
                          value={opt}
                          placeholder={`Option ${optIndex + 1}`}
                          onChange={(e) => updateQuestionOption(grp.tempId, q.tempId, optIndex, e.target.value)}
                          style={{ padding: '5px', width: '200px' }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="question-actions" style={{ marginTop: '10px' }}>
                  <button onClick={() => deleteQuestion(grp.tempId, q.tempId)}>❌️</button>
                  <select
                    value={q.type}
                    onChange={e => {
                      const type = e.target.value;
                      setDraft(prev => ({
                        ...prev,
                        groups: prev.groups.map(g => {
                          if (g.tempId !== grp.tempId) return g;
                          return {
                            ...g,
                            questions: g.questions.map(qq =>
                              qq.tempId === q.tempId
                                ? {
                                    ...qq,
                                    type,
                                    options: type === "mcq"
                                        ? ["Strongly disagree", "Disagree", "Agree", "Strongly agree"]
                                        : null,
                                    isModified: true
                                  }
                                : qq
                            )
                          };
                        })
                      }));
                    }}
                  >
                    <option value="mcq">MCQ</option>
                    <option value="dual_textbox">Dual Textbox</option>
                    <option value="textbox">Textbox</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <button className="add-group-btn" onClick={addGroup}>
        + Add Group
      </button>
      <button className="save-btn" onClick={saveSurvey}>
        💾 Save Changes
      </button>
    </div>
  );
}